import { Children } from 'react';
import styled from 'styled-components';
import { Skeleton } from 'antd';
import { P, fs, radius, shadow, ease, card, tint, tintB, tintT } from '../theme/tokens';

export const Board = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 12px;
`;

const Lane = styled.section`
  ${card}
  flex: 0 0 ${(p) => p.$width || 264}px;
  border-top: ${(p) => (p.$color ? `3px solid ${p.$color}` : 'none')};

  header {
    padding: 12px 14px 10px;
    border-bottom: 1px solid ${P.line};
  }
  h2 {
    margin: 0;
    font-size: ${fs.sm}px;
    font-weight: 700;
    line-height: 1.3;
    color: ${P.ink};
  }
  header span {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: ${P.subtle};
    font-variant-numeric: tabular-nums;
  }
`;

const LaneNote = styled.p`
  margin: 0;
  padding: 8px 14px;
  font-size: 11px;
  line-height: 1.45;
  color: ${(p) => tintT(p.$color)};
  background: ${(p) => tint(p.$color)};
  border-bottom: 1px solid ${P.line};
`;

const LaneList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 10px;
  list-style: none;
`;

const LaneEmpty = styled.p`
  margin: 0;
  padding: 30px 14px;
  text-align: center;
  font-size: ${fs.xs}px;
  color: ${P.subtle};
`;

export function BoardColumn({ id, label, color, count, note, loading, emptyText = 'Nothing here', width, maxHeight, children, ...rest }) {
  const scroll = maxHeight ? { maxHeight, overflowY: 'auto' } : undefined;
  return (
    <Lane $color={color} $width={width} aria-labelledby={`lane-${id}`} {...rest}>
      <header>
        <h2 id={`lane-${id}`}>{label}</h2>
        <span>{loading ? 'Loading…' : count}</span>
      </header>
      {note && <LaneNote $color={color || P.subtle}>{note}</LaneNote>}
      {loading ? (
        <LaneList as="div">
          <Skeleton active title={false} paragraph={{ rows: 3 }} />
        </LaneList>
      ) : Children.count(children) ? (
        <LaneList style={scroll}>{children}</LaneList>
      ) : (
        <LaneEmpty>{emptyText}</LaneEmpty>
      )}
    </Lane>
  );
}

export const BoardCard = styled.button`
  display: block;
  width: 100%;
  padding: 10px 11px;
  text-align: left;
  font-family: inherit;
  background: ${(p) => (p.$accent ? `${p.$accent}0A` : P.bgSubtle)};
  border: 1px solid ${(p) => (p.$accent ? `${p.$accent}33` : P.border)};
  border-radius: ${radius.md}px;
  box-shadow: ${(p) => (p.$accent ? `inset 3px 0 0 ${p.$accent}` : 'none')};
  cursor: pointer;
  transition: box-shadow 150ms ${ease};

  &:hover {
    box-shadow: ${(p) => (p.$accent ? `inset 3px 0 0 ${p.$accent}, ` : '')}${shadow.md};
  }
  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
`;

export const BoardCardTop = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;

  strong {
    font-size: ${fs.sm}px;
    font-weight: 700;
    color: ${P.ink};
    text-wrap: pretty;
  }
  b {
    flex: none;
    font-size: ${fs.xs}px;
    font-weight: 700;
    color: ${P.navy};
    font-variant-numeric: tabular-nums;
  }
`;

export const BoardCardLine = styled.span`
  display: block;
  margin-top: ${(p) => (p.$first ? 4 : 0)}px;
  font-size: 11px;
  line-height: 1.5;
  color: ${P.subtle};
`;

export const BoardPills = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;

  &:empty {
    display: none;
  }
`;

export const BoardPill = styled.span`
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 700;
  color: ${(p) => (p.$tint ? tintT(p.$tint) : p.$color || P.muted)};
  background: ${(p) => (p.$tint ? tint(p.$tint) : P.white)};
  border: 1px solid ${(p) => (p.$tint ? tintB(p.$tint) : P.border)};
  border-radius: ${radius.pill}px;
`;
